import React, { useEffect, useState } from 'react';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import { trackAmplitudeEvent } from './analytics';
import { type EvalConfig, evalErrorMessage } from './evaluationTypes';
import './evaluate.css';

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

async function readJSON(res: Response) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export function useEvalConfig(apiFetch: ApiFetch, userId?: string) {
  const [config, setConfig] = useState<EvalConfig | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/evaluations/config')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (!cancelled && data) setConfig(data); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [apiFetch, userId]);
  return config;
}

// ---------------------------------------------------------------------------
// Entry panel (first tab) and fake door
// ---------------------------------------------------------------------------

export function EvaluateLocalStart({ config, onStart, onOpenList, signedIn, apiFetch }: {
  config: EvalConfig;
  onStart: () => void;
  onOpenList: () => void;
  signedIn: boolean;
  apiFetch: ApiFetch;
}) {
  if (!config.enabled) return <EarlyAccessForm apiFetch={apiFetch} />;
  return (
    <section className="ev-start" aria-labelledby="ev-start-title">
      <h2 id="ev-start-title" className="ev-start-title">Antes de firmar el contrato, revisa el local</h2>
      <ol className="ev-start-steps">
        <li><b>1.</b> Pon la dirección y tu rubro.</li>
        <li><b>2.</b> Escribe el alquiler. Lo demás lo completamos con valores típicos.</li>
        <li><b>3.</b> Ve gratis si te conviene: <span className="ev-dot ev-dot-green" /> <span className="ev-dot ev-dot-amber" /> <span className="ev-dot ev-dot-red" /></li>
      </ol>
      <button type="button" className="ev-cta" onClick={onStart}>Evaluar mi local <ChevronRight size={18} aria-hidden="true" /></button>
      {signedIn && <button type="button" className="ev-link" onClick={onOpenList}>Ver mis evaluaciones</button>}
    </section>
  );
}

export function EarlyAccessForm({ apiFetch, rubro = '' }: { apiFetch: ApiFetch; rubro?: string }) {
  const [form, setForm] = useState({ rubro, address: '', rent: '', whatsapp: '' });
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setState('sending');
    try {
      const res = await apiFetch('/api/evaluations/early-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, rent: Number(form.rent) || 0 }),
      });
      const data = await readJSON(res);
      if (!res.ok) throw new Error(evalErrorMessage(data, 'No pudimos guardar tus datos. Intenta de nuevo.'));
      trackAmplitudeEvent('eval_fake_door_submit', { rubro: form.rubro });
      setState('sent');
    } catch (err: any) {
      setError(err.message);
      setState('idle');
    }
  };
  if (state === 'sent') {
    return (
      <section className="ev-start">
        <CheckCircle2 size={28} className="ev-ok" aria-hidden="true" />
        <p className="ev-start-title">Acceso anticipado: te enviamos tu evaluación por WhatsApp en menos de 24 h.</p>
      </section>
    );
  }
  return (
    <form className="ev-start ev-form" onSubmit={submit}>
      <h2 className="ev-start-title">¿Vas a alquilar un local? Evalúalo antes de firmar</h2>
      <label>Rubro<input required value={form.rubro} onChange={(e) => setForm({ ...form, rubro: e.target.value })} placeholder="Botica, cafetería, bodega…" /></label>
      <label>Dirección o distrito<input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Av. Larco 345, Miraflores" /></label>
      <label>Alquiler mensual (S/)<input inputMode="numeric" value={form.rent} onChange={(e) => setForm({ ...form, rent: e.target.value.replace(/[^\d.]/g, '') })} placeholder="4500" /></label>
      <label>WhatsApp<input required inputMode="tel" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="999 888 777" /></label>
      {error && <p className="ev-field-error">{error}</p>}
      <button type="submit" className="ev-cta" disabled={state === 'sending'}>{state === 'sending' ? 'Enviando…' : 'Quiero mi evaluación'}</button>
    </form>
  );
}

